/**
 * Request types the backend has retired: these requests are CUSTOM and named by their Commenda service. Creation
 * still sends the old strings until the backend takes the service directly, and rows that still carry one resolve to it.
 */
export const RetiredServiceRequestType = {
  US_TA_ONBOARDING: 'US_TA_ONBOARDING',
  IN_TA_ONBOARDING: 'IN_TA_ONBOARDING',
  CA_TA_ONBOARDING: 'CA_TA_ONBOARDING',
  GB_TA_ONBOARDING: 'GB_TA_ONBOARDING',
  IE_TA_ONBOARDING: 'IE_TA_ONBOARDING',
  SG_TA_ONBOARDING: 'SG_TA_ONBOARDING',
  TRANSFER_PRICING_BENCHMARKING: 'TRANSFER_PRICING_BENCHMARKING',
  TRANSFER_PRICING_POLICY: 'TRANSFER_PRICING_POLICY',
  INDIA_PVT_LTD_INCORPORATION: 'INDIA_PVT_LTD_INCORPORATION',
  INDIA_LLP_INCORPORATION: 'INDIA_LLP_INCORPORATION',
  US_LLC_INCORPORATION: 'US_LLC_INCORPORATION',
  US_CCORP_INCORPORATION: 'US_CCORP_INCORPORATION',
  CA_CORP_INCORPORATION: 'CA_CORP_INCORPORATION',
  UAE_INCORPORATION: 'UAE_INCORPORATION',
  UK_INCORPORATION: 'UK_INCORPORATION',
  IE_INCORPORATION: 'IE_INCORPORATION',
  SG_INCORPORATION: 'SG_INCORPORATION',
  VAT_RETURN_FILING_DEFAULT: 'VAT_RETURN_FILING_DEFAULT',
  BOOKKEEPING_REQUEST: 'BOOKKEEPING_REQUEST',
  INDIRECT_TAX_UPLOAD_TRANSACTION: 'INDIRECT_TAX_UPLOAD_TRANSACTION',
} as const;

export type TRetiredServiceRequestType =
  (typeof RetiredServiceRequestType)[keyof typeof RetiredServiceRequestType];

// Exposure requests are never created again, but rows can still carry their type.
export const RETIRED_SERVICE_REQUEST_TYPE_SERVICE_ID: Record<
  TRetiredServiceRequestType | 'INDIRECT_TAX_EXPOSURE',
  string
> = {
  US_TA_ONBOARDING: 'OB_TNA_US',
  IN_TA_ONBOARDING: 'OB_TNA_IN',
  CA_TA_ONBOARDING: 'OB_TNA_CA',
  GB_TA_ONBOARDING: 'OB_TNA_GB',
  IE_TA_ONBOARDING: 'OB_TNA_IE',
  SG_TA_ONBOARDING: 'OB_TNA_SG',
  TRANSFER_PRICING_BENCHMARKING: 'TP_BEN',
  TRANSFER_PRICING_POLICY: 'TP_POL',
  INDIA_PVT_LTD_INCORPORATION: 'EM_INC_IN_PVTLTD',
  INDIA_LLP_INCORPORATION: 'EM_INC_IN_LLP',
  US_LLC_INCORPORATION: 'EM_INC_US_LLC',
  US_CCORP_INCORPORATION: 'EM_INC_US_CCORP',
  CA_CORP_INCORPORATION: 'EM_INC_CA',
  UAE_INCORPORATION: 'EM_INC_AE',
  UK_INCORPORATION: 'EM_INC_GB',
  IE_INCORPORATION: 'EM_INC_IE',
  SG_INCORPORATION: 'EM_INC_SG',
  VAT_RETURN_FILING_DEFAULT: 'IT_FIL_GB_VAT',
  BOOKKEEPING_REQUEST: 'AC_BK',
  INDIRECT_TAX_UPLOAD_TRANSACTION: 'IT_UPL_US',
  INDIRECT_TAX_EXPOSURE: 'IT_NEX_EXPOSURE',
};

export const VAT_SERVICE_ID_BY_COUNTRY = {
  GB: 'IT_FIL_GB_VAT',
  DE: 'IT_FIL_DE_VAT',
} as const;

export const BOOKKEEPING_SERVICE_IDS = ['AC_BK', 'AC_BK_IN'];

export const INDIRECT_TAX_EXPOSURE_SERVICE_ID = 'IT_NEX_EXPOSURE';

export const INDIRECT_TAX_UPLOAD_SERVICE_ID = 'IT_UPL_US';

/** The Commenda service a request belongs to. */
export const getServiceRequestServiceId = (
  serviceRequest:
    | {
        serviceCompositeId?: string | null;
        commendaService?: { serviceCompositeId: string } | null;
      }
    | null
    | undefined
): string | undefined =>
  serviceRequest?.commendaService?.serviceCompositeId ??
  serviceRequest?.serviceCompositeId ??
  undefined;

/**
 * The sales-tax flows. Their requests are CUSTOM requests of the service of where they're made, and are told apart by
 * the filing or registration they're linked to.
 */
export const SalesTaxFlow = {
  SALES_TAX_FILING: 'SALES_TAX_FILING',
  SALES_TAX_REGISTRATION: 'SALES_TAX_REGISTRATION',
} as const;

export type TSalesTaxFlow = (typeof SalesTaxFlow)[keyof typeof SalesTaxFlow];

export const isSalesTaxFlow = (value: unknown): value is TSalesTaxFlow =>
  Object.values<unknown>(SalesTaxFlow).includes(value);

/** The sales-tax flow a request belongs to: the filing or registration it's linked to. */
export const getServiceRequestSalesTaxFlow = (
  serviceRequest:
    | {
        salesTaxFlow?: TSalesTaxFlow | null;
        salesTaxFiling?: unknown;
        salesTaxFilingId?: string | null;
        salesTaxNewRegistration?: unknown;
        salesTaxRegistrationId?: string | null;
      }
    | null
    | undefined
): TSalesTaxFlow | undefined => {
  // The flow the backend computed — the filing or registration's, else the request's workflow's — wins.
  if (serviceRequest?.salesTaxFlow) return serviceRequest.salesTaxFlow;
  if (serviceRequest?.salesTaxFiling || serviceRequest?.salesTaxFilingId)
    return SalesTaxFlow.SALES_TAX_FILING;
  if (
    serviceRequest?.salesTaxNewRegistration ||
    serviceRequest?.salesTaxRegistrationId
  )
    return SalesTaxFlow.SALES_TAX_REGISTRATION;
  return undefined;
};
